import { useEffect, useState } from 'react';
import type { Agent, EvalCase, EvalResult, EvalSet } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

export function EvalsPage() {
  const [sets, setSets] = useState<EvalSet[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const [curating, setCurating] = useState(false);
  const [latest, setLatest] = useState<Record<string, EvalResult>>({});
  const [results, setResults] = useState<Record<string, EvalResult[]>>({});
  const [cases, setCases] = useState<Record<string, EvalCase[]>>({});
  const [expandedCases, setExpandedCases] = useState<string | null>(null);
  const [expandedResults, setExpandedResults] = useState<string | null>(null);
  const [agentId, setAgentId] = useState('');
  const [name, setName] = useState('');
  const [threshold, setThreshold] = useState(0.8);
  const [caseInput, setCaseInput] = useState<Record<string, string>>({});
  const [caseExpected, setCaseExpected] = useState<Record<string, string>>({});
  const [pendingDelete, setPendingDelete] = useState<EvalSet | null>(null);

  function load() {
    void apiClient.listEvalSets().then(setSets).catch(() => setSets([]));
  }

  useEffect(() => {
    load();
    void apiClient.listAgents().then((page) => setAgents(page.items)).catch(() => setAgents([]));
  }, []);

  function agentName(id: string): string {
    return agents.find((agent) => agent.id === id)?.name ?? id.slice(0, 8);
  }

  async function createSet() {
    if (saving) return;
    setSaving(true);
    try {
      await apiClient.createEvalSet({ agentId, name: name.trim(), passThreshold: Number(threshold) });
      setShowCreate(false);
      setName('');
      setAgentId('');
      load();
    } finally {
      setSaving(false);
    }
  }

  async function toggleCases(setId: string) {
    if (expandedCases === setId) {
      setExpandedCases(null);
      return;
    }
    setExpandedCases(setId);
    setExpandedResults(null);
    const rows = await apiClient.listEvalCases(setId);
    setCases((current) => ({ ...current, [setId]: rows }));
  }

  async function addCase(set: EvalSet) {
    const input = (caseInput[set.id] || '').trim();
    if (!input) return;
    const expectedContains = (caseExpected[set.id] || '').split(',').map((part) => part.trim()).filter(Boolean);
    await apiClient.addEvalCase(set.id, { input, expectedContains });
    setCaseInput((current) => ({ ...current, [set.id]: '' }));
    setCaseExpected((current) => ({ ...current, [set.id]: '' }));
    setExpandedCases(null);
    await toggleCases(set.id);
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Evaluations</h1>
          <p className="text-sm text-gray-500">Golden-case test sets that gate config changes. The Evolution engine runs these before applying prompt/rule/triage/KB changes. Build sets here, add cases, and run them on demand.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={curating} onClick={() => {
            setCurating(true);
            const agentIds = [...new Set(sets.map((set) => set.agentId))];
            if (!agentIds.length) {
              setCurating(false);
              return;
            }
            void Promise.all(agentIds.map((id) => apiClient.curateGoldenCandidates(id).catch(() => null))).finally(() => {
              setCurating(false);
              load();
            });
          }}>{curating ? 'Curating...' : 'Curate golden'}</Button>
          <Button size="sm" onClick={() => setShowCreate((value) => !value)}>New eval set</Button>
        </div>
      </div>
      {showCreate && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-base font-semibold">Create eval set</h2>
          <label className="mt-3 block text-xs text-gray-500">Agent</label>
          <select className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" value={agentId} onChange={(event) => setAgentId(event.target.value)}>
            <option value="">Select agent</option>
            {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
          </select>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <input className="rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" value={name} onChange={(event) => setName(event.target.value)} placeholder="Refund golden set" />
            <input type="number" step="0.05" min="0" max="1" className="rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" value={threshold} onChange={(event) => setThreshold(Number(event.target.value))} />
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" disabled={!agentId || !name.trim() || saving} onClick={() => void createSet()}>{saving ? 'Creating...' : 'Create'}</Button>
          </div>
        </div>
      )}
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {sets.length === 0 && <p className="text-sm text-gray-500">No eval sets yet.</p>}
        {sets.map((set) => {
          const result = latest[set.id];
          return (
            <div key={set.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <strong>{set.name}</strong>
                  <div className="text-xs text-gray-500">agent: {agentName(set.agentId)} · threshold {set.passThreshold}{set.workspaceId ? ' · scoped' : ''}</div>
                </div>
                <div className="flex items-center gap-1">
                  {result && (
                    <button type="button" onClick={() => {
                      if (expandedResults === set.id) {
                        setExpandedResults(null);
                        return;
                      }
                      setExpandedResults(set.id);
                      setExpandedCases(null);
                      void apiClient.listEvalResults(set.id).then((rows) => setResults((current) => ({ ...current, [set.id]: rows })));
                    }}>
                      <Badge variant={result.passed ? 'success' : 'destructive'}>{(result.passRate * 100).toFixed(0)}% · {result.passed ? 'pass' : 'fail'}</Badge>
                    </button>
                  )}
                  <Button size="sm" variant="secondary" disabled={running === set.id} onClick={() => {
                    setRunning(set.id);
                    void apiClient.runEvalSet(set.id).then((row) => setLatest((current) => ({ ...current, [set.id]: row }))).finally(() => setRunning(null));
                  }}>{running === set.id ? 'Running...' : 'Run'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => void toggleCases(set.id)}>Cases</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setPendingDelete(set)}>x</Button>
                </div>
              </div>
              {expandedCases === set.id && (
                <div className="mt-2 space-y-2">
                  {(cases[set.id] ?? []).map((row) => (
                    <div key={row.id} className="rounded bg-gray-50 p-2 text-xs dark:bg-gray-800">
                      <div className="flex justify-between">
                        <span className="truncate font-medium">{row.input.slice(0, 80)}{row.input.length > 80 ? '...' : ''}</span>
                        <button type="button" className="text-red-600" onClick={() => void apiClient.deleteEvalCase(row.id).then(() => toggleCases(set.id))}>x</button>
                      </div>
                      <div className="text-gray-500">expects: {((row.expectedContains as string[]) ?? []).join(', ') || '-'}</div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800" value={caseInput[set.id] || ''} onChange={(event) => setCaseInput((current) => ({ ...current, [set.id]: event.target.value }))} placeholder="Input message" />
                    <input className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800" value={caseExpected[set.id] || ''} onChange={(event) => setCaseExpected((current) => ({ ...current, [set.id]: event.target.value }))} placeholder="Expected substrings (comma-separated)" />
                    <Button size="sm" variant="outline" disabled={!(caseInput[set.id] || '').trim()} onClick={() => void addCase(set)}>Add</Button>
                  </div>
                </div>
              )}
              {expandedResults === set.id && (
                <div className="mt-2 space-y-2">
                  {(results[set.id] ?? []).map((row) => (
                    <div key={row.id} className="rounded bg-gray-50 p-2 text-xs dark:bg-gray-800">
                      <span className={row.passed ? 'text-green-600' : 'text-red-600'}>{(row.passRate * 100).toFixed(0)}%</span>
                      {' · '}{row.total} cases · {new Date(row.createdAt).toLocaleString()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        message={pendingDelete ? `Delete eval set "${pendingDelete.name}" and all its cases/results?` : ''}
        confirmText="Delete"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          void apiClient.deleteEvalSet(pendingDelete.id).then(() => load());
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
